import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Breadcrumbs, Link } from '@mui/material';
import { Star as StarIcon, NavigateNext as NavigateNextIcon } from '@mui/icons-material';
import { useRecipe } from '../../context/RecipeContext';
import lowFodmapBackground from '../../images/low-fodmap.png';
import lowFODMAPDietFoodsImage from '../../images/Low-FODMAP-Diet-Foods.jpg';
import foodmapFoodsImage from '../../images/foodmap-foods.png';
import lowfoodmapHealthImage from '../../images/lowfoodmap-health.png';

const PAGE_URL = 'https://createadish.com/dietary-guide/low-fodmap';
const PAGE_TITLE = 'Low-FODMAP Diet Guide: For Gut Health - Create A Dish';
const PAGE_DESCRIPTION = 'A temporary restrictive diet used to identify trigger foods for people with IBS by limiting certain fermentable carbohydrates.';
const PAGE_IMAGE = 'https://createadish.com/images/low-fodmap-og.png';

const LowFODMAP: React.FC = () => {
  const navigate = useNavigate();
  const { setMode } = useRecipe();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        <meta name="keywords" content="low FODMAP diet, IBS diet, gut health, digestive health, FODMAP, irritable bowel syndrome" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESCRIPTION} />
        <meta property="og:image" content={PAGE_IMAGE} />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href={PAGE_URL} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "Low-FODMAP Diet Guide: For Gut Health",
            "description": PAGE_DESCRIPTION,
            "image": PAGE_IMAGE,
            "author": { "@type": "Organization", "name": "Create A Dish" },
            "publisher": { "@type": "Organization", "name": "Create A Dish", "logo": { "@type": "ImageObject", "url": "https://createadish.com/logo.png" } },
            "datePublished": "2025-12-24",
            "mainEntityOfPage": { "@type": "WebPage", "@id": PAGE_URL },
            "keywords": "GutHealth, IBS, Specialized"
          })}
        </script>
      </Helmet>
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', maxWidth: '1200px', mx: 'auto' }}>
        <Box sx={{ height: { xs: '40vh', md: '50vh' }, backgroundImage: `url(${lowFodmapBackground})`, backgroundSize: 'cover', backgroundPosition: 'center', filter: 'blur(1px)', position: 'relative' }}>
          <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0, 0, 0, 0.5)', px: 2, textAlign: 'center' }}>
            <Typography variant="h3" component="h1" sx={{ color: 'white', fontWeight: 'bold', textShadow: '2px 2px 4px rgba(0,0,0,0.8)', mb: 2 }}>Low-FODMAP Diet</Typography>
            <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold', textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}>For Gut Health</Typography>
          </Box>
        </Box>
        <Box sx={{ p: { xs: 2, md: 4 }, flexGrow: 1 }}>
          <Breadcrumbs 
            separator={<NavigateNextIcon fontSize="small" />} 
            sx={{ 
              mb: 4,
              '& .MuiBreadcrumbs-separator': {
                color: 'primary.main'
              }
            }}
            aria-label="breadcrumb"
          >
            <Link
              underline="hover"
              onClick={() => navigate('/resources')}
              sx={{ 
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500,
                color: 'text.primary',
                '&:hover': {
                  color: 'primary.main'
                }
              }}
            >
              Resources
            </Link>
            <Link
              underline="hover"
              onClick={() => navigate('/dietary-guide')}
              sx={{ 
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500,
                color: 'text.primary',
                '&:hover': {
                  color: 'primary.main'
                }
              }}
            >
              Dietary Guide
            </Link>
            <Typography 
              sx={{ 
                fontSize: '1rem',
                fontWeight: 600,
                color: 'primary.main'
              }}
            >
              Low-FODMAP Diet
            </Typography>
          </Breadcrumbs>
          <Typography variant="h4" component="h2" sx={{ textAlign: 'center', mb: 4, fontWeight: 'bold', color: 'primary.main' }}>The Low-FODMAP Diet</Typography>
          <Typography variant="body1" sx={{ mb: 4, textAlign: 'center', maxWidth: '800px', mx: 'auto', lineHeight: 1.6, fontSize: { xs: '1rem', md: '1.1rem' } }}>{PAGE_DESCRIPTION}</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 4, flexWrap: 'wrap' }}>
            {['#GutHealth', '#IBS', '#Specialized'].map(tag => <Typography key={tag} variant="body2" sx={{ color: 'primary.main', fontWeight: 'bold' }}>{tag}</Typography>)}
          </Box>
          <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.1rem' } }}>
            Welcome to CreateADish.com's guide to the Low-FODMAP Diet, a temporary restrictive diet used to identify trigger foods for people with IBS by limiting certain fermentable carbohydrates. Developed as an evidence-based approach to manage gastrointestinal symptoms, it's typically followed in three phases under professional guidance.
          </Typography>
          <Box component="img" src={lowFODMAPDietFoodsImage} alt="Low-FODMAP diet foods" sx={{ width: '100%', maxWidth: 800, mx: 'auto', my: 3, borderRadius: 2, boxShadow: 3, display: 'block' }} />

          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              Historical Background
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.1rem' } }}>
              The Low-FODMAP Diet was pioneered by researchers at Monash University in Melbourne, Australia, in the early 2000s. Dr. Sue Shepherd and Professor Peter Gibson formalized the concept around 2005, based on studies showing that certain short-chain carbohydrates ferment in the gut, causing IBS symptoms.
            </Typography>
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              Key Components and Foods
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.1rem' } }}>
              The diet involves three phases: Elimination (2-6 weeks of strict low-FODMAP eating), Reintroduction (testing high-FODMAP foods), and Maintenance (personalized long-term plan).
            </Typography>
            
            <Typography variant="h5" component="h4" sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
              Core Foods to Emphasize
            </Typography>
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
              <Typography component="li" sx={{ mb: 1 }}><strong>Fruits</strong>: Strawberries, blueberries, oranges, kiwi (limited portions).</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Vegetables</strong>: Carrots, spinach, zucchini, bell peppers, cucumbers, potatoes.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Proteins</strong>: Meats (beef, chicken, turkey), fish, eggs, firm tofu, tempeh.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Grains</strong>: Oats, quinoa, rice, gluten-free bread (check labels).</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Dairy Alternatives</strong>: Lactose-free milk, almond milk (limited), hard cheeses.</Typography>
            </Box>
            <Box component="img" src={foodmapFoodsImage} alt="FODMAP foods chart" sx={{ width: '100%', maxWidth: 800, mx: 'auto', my: 3, borderRadius: 2, boxShadow: 3, display: 'block' }} />
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              Health Benefits
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.1rem' } }}>
              The Low-FODMAP Diet is effective for reducing IBS symptoms like bloating, gas, pain, and irregular bowel movements in about 70% of sufferers. It helps identify triggers, improves gut health, and may alleviate related conditions like SIBO.
            </Typography>
            <Box component="img" src={lowfoodmapHealthImage} alt="Low-FODMAP health benefits" sx={{ width: '100%', maxWidth: 800, mx: 'auto', my: 3, borderRadius: 2, boxShadow: 3, display: 'block' }} />
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              Tips for Adopting the Low-FODMAP Diet
            </Typography>
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
              <Typography component="li" sx={{ mb: 1 }}><strong>Work with a Professional</strong>: Consult a registered dietitian to navigate phases.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Track Symptoms</strong>: Keep a food diary during reintroduction to identify triggers.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Portion Control</strong>: Even low-FODMAP foods can be high in larger amounts.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Use AI</strong>: Generate recipes on CreateADish.com, such as "low-FODMAP chicken stir-fry."</Typography>
            </Box>
          </Box>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h5" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>Ready to Create Your Perfect Dish?</Typography>
            <Button variant="contained" size="large" startIcon={<StarIcon />} onClick={() => { setMode('generate'); navigate('/composer'); }} sx={{ minWidth: 200, fontSize: '1.1rem', py: 1.5, px: 4 }}>Get Started</Button>
          </Box>
          <Box sx={{ textAlign: 'center', py: 3, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary">© 2025 Create A Dish. Made with ❤️ for food lovers everywhere.</Typography>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default LowFODMAP;
